// packages/web/src/services/firebaseWeb.ts
import { initializeApp } from 'firebase/app';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getAuth, connectAuthEmulator, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { firebaseConfig, useEmulator } from '@matchpoint/shared';

class FirebaseWebService {
  private app: any;
  private db: any;
  private auth: any;
  private functions: any;
  private initialized = false;

  async initializeApp(): Promise<void> {
    if (this.initialized) return;

    this.app = initializeApp(firebaseConfig);
    this.db = getFirestore(this.app);
    this.auth = getAuth(this.app);
    this.functions = getFunctions(this.app);

    // CRITICAL: Set auth persistence to localStorage
    // This is what makes auth survive page refreshes!
    try {
      await setPersistence(this.auth, browserLocalPersistence);
      console.log('✅ Auth persistence set to browserLocalPersistence');
    } catch (error) {
      console.error('❌ Failed to set auth persistence:', error);
    }

    // Connect to emulators in development (only if not already connected)
    if (useEmulator) {
      try {
        // Check if emulator is already connected by looking at the auth config
        const authConfig = (this.auth as any).config;
        const isEmulatorConnected = authConfig?.emulator !== null && authConfig?.emulator !== undefined;
        
        if (!isEmulatorConnected) {
          connectAuthEmulator(this.auth, 'http://localhost:9099', { disableWarnings: true });
          console.log('✅ Connected Auth Emulator');
        } else {
          console.log('ℹ️ Auth Emulator already connected');
        }
        
        // For Firestore and Functions, check the internal state
        // Firestore uses _settings._host to check
        const firestoreSettings = (this.db as any)._settings;
        if (!firestoreSettings.host.includes('localhost')) {
          connectFirestoreEmulator(this.db, 'localhost', 8080);
          console.log('✅ Connected Firestore Emulator');
        } else {
          console.log('ℹ️ Firestore Emulator already connected');
        }
        
        // Functions checks can be done similarly
        const functionsCustomDomain = (this.functions as any)._customDomain;
        if (!functionsCustomDomain || !functionsCustomDomain.includes('localhost')) {
          connectFunctionsEmulator(this.functions, 'localhost', 5001);
          console.log('✅ Connected Functions Emulator');
        } else {
          console.log('ℹ️ Functions Emulator already connected');
        }
      } catch (error) {
        // Silently ignore emulator connection errors if already connected
        const errorMessage = error instanceof Error ? error.message : String(error);
        if (!errorMessage.includes('emulator-config-failed')) {
          console.warn('⚠️ Failed to connect to emulators:', error);
        } else {
          console.log('ℹ️ Emulators already configured');
        }
      }
    }

    this.initialized = true;
  }

  getFirestore() {
    if (!this.db) throw new Error('Firestore not initialized');
    return this.db;
  }

  getAuth() {
    if (!this.auth) throw new Error('Auth not initialized');
    return this.auth;
  }

  getFunctions() {
    if (!this.functions) throw new Error('Functions not initialized');
    return this.functions;
  }
}

// Export singleton instance
export const firebaseService = new FirebaseWebService();